import type { CampaignRequest } from "@/hooks/api/use-campaign-requests";

/**
 * The two ticks the client makes on step 4 and repeats on step 8.
 *
 * They are recorded the moment they are made. `POST /campaign-requests/:id/attest` stamps
 * `leadConsentConfirmedAt` and `estimatesAcknowledgedAt` with the API's own clock, which is what
 * UX §07 note 3 means by "a checkbox with a date stamp behind it" — a consent record carrying a
 * time the client's browser chose is worth nothing in the conversation the record exists for.
 *
 * This module used to keep them in session storage instead, because nothing sent them: the tick
 * lived in one browser until submit, so a client who ticked the consent box on step 4 and came
 * back the next day on another machine had attested to nothing, and the review screen printed
 * "Not recorded" over a box they remembered ticking. There is nothing left to cache — the record
 * is on the request, and the request is what every screen already reads.
 *
 * An attestation cannot be unmade. The API fills nulls only, so a stamped date is fixed: the box
 * behind it is checked and disabled, which is the honest rendering of "this is on the record".
 */

export interface DraftAttestations {
	leadConsentConfirmed: boolean;
	estimatesAcknowledged: boolean;
}

/** What the request says has been attested to. The only source there is. */
export function readAttestations(
	request: Pick<CampaignRequest, "leadConsentConfirmedAt" | "estimatesAcknowledgedAt">
): DraftAttestations {
	return {
		leadConsentConfirmed: Boolean(request.leadConsentConfirmedAt),
		estimatesAcknowledged: Boolean(request.estimatesAcknowledgedAt),
	};
}
